import { isNumber } from "lodash";
import { useTranslation } from "react-i18next";
import { Label, Popup } from "semantic-ui-react";

import { PinTypeIcon } from "components";
import "components/Pin/Pin.scss";
import { PIN_STATUSES, PIN_TYPES } from "models";
import { isAppointmentWarrantyPinSignatureMissing, isWarrantyPinSignatureRequiredForVin } from "utils/appointmentUtils";

import type { Appointment, Brand, DealerLocation, Intervention, Pin as PinModel, QuestionResult } from "models";
import type { SemanticCOLORS } from "semantic-ui-react";

export interface PinModelWithHistory extends PinModel {
  history: PinModel[];
}

type PinProps = {
  pin: PinModelWithHistory;
  vin: string;
  location: DealerLocation;
  brands: Brand[];
  intervention?: Intervention;
  questionResult?: QuestionResult;
  onClick?: (evt: React.MouseEvent<HTMLElement>) => void;
  readOnly?: boolean;
};

type PinStatusTranslationLookup = {
  [key in PIN_STATUSES]: { translation_key: string; default_message: string };
};

type WarrantyPinParams = {
  pin: PinModel;
  vin: string;
  location: DealerLocation | null;
  brands: Brand[];
  intervention?: Intervention;
  questionResult?: QuestionResult;
};

export const pinStatusTranslations: PinStatusTranslationLookup = {
  [PIN_STATUSES.NotSet]: { translation_key: "v8_not_set", default_message: "Not Set" },
  [PIN_STATUSES.Deleted]: { translation_key: "v8_deleted", default_message: "Deleted" },
  [PIN_STATUSES.Support]: { translation_key: "v8_support", default_message: "Support" },
  [PIN_STATUSES.ApprovalPending]: { translation_key: "v8_approval_pending", default_message: "Approval Pending" },
  [PIN_STATUSES.MoreInfo]: { translation_key: "v8_more_information_required", default_message: "More Information Required" },
  [PIN_STATUSES.Approved]: { translation_key: "v8_approved", default_message: "Approved" },
  [PIN_STATUSES.Declined]: { translation_key: "v8_declined", default_message: "Declined" },
  [PIN_STATUSES.ToClaim]: { translation_key: "v8_to_claim", default_message: "To Claim" },
  [PIN_STATUSES.Claimed]: { translation_key: "v8_claimed", default_message: "Claimed" },
  [PIN_STATUSES.Billed]: { translation_key: "v8_billed", default_message: "Billed" },
  [PIN_STATUSES.Request]: { translation_key: "v8_request", default_message: "Request" },
  [PIN_STATUSES.TechnicalInfo]: { translation_key: "v8_technical_info", default_message: "Technical Info" }
};

const isWarrantyPinSignatureMissing = ({ pin, vin, location, brands, intervention, questionResult }: WarrantyPinParams) =>
  location?.is_warranty_pin_signature_missing_warning_enabled &&
  pin.pin_type_id === PIN_TYPES.Warranty &&
  ((intervention && !intervention.is_signed_by_customer) || (questionResult && !questionResult.is_signed_by_customer)) &&
  isWarrantyPinSignatureRequiredForVin(vin, location, brands);

export const getPinColor = ({ pin, vin, location, brands, intervention, questionResult }: WarrantyPinParams): SemanticCOLORS => {
  if (isWarrantyPinSignatureMissing({ pin, vin, location, brands, intervention, questionResult })) return "red";

  if (pin.pin_type_id === PIN_TYPES.Warranty) {
    if (location?.is_warranty_pin_support_warning_enabled && !pin.support_nr) return "orange";
    if (location?.is_warranty_pin_claim_warning_enabled && !pin.claim_nr) return "orange";
  }

  if (pin.pin_type_id === PIN_TYPES.Recall) {
    if (location?.is_recall_pin_support_warning_enabled && !pin.support_nr) return "orange";
    if (location?.is_recall_pin_claim_warning_enabled && !pin.claim_nr) return "orange";
  }

  return "blue";
};

export const getAppointmentPinColor = (appointment: Appointment, brands: Brand[], location?: DealerLocation | null): SemanticCOLORS => {
  if (!location) return "blue";

  if (isAppointmentWarrantyPinSignatureMissing(appointment, brands, location)) return "red";

  if (location.is_warranty_pin_support_warning_enabled && appointment.warranty_pin_support_nr_missing_count) return "orange";
  if (location.is_warranty_pin_claim_warning_enabled && appointment.warranty_pin_claim_nr_missing_count) return "orange";

  if (location.is_recall_pin_support_warning_enabled && appointment.recall_pin_support_nr_missing_count) return "orange";
  if (location.is_recall_pin_claim_warning_enabled && appointment.recall_pin_claim_nr_missing_count) return "orange";

  return "blue";
};

export const Pin = ({ location, brands, intervention, pin, onClick, vin, questionResult, readOnly = false }: PinProps) => {
  const { t } = useTranslation();

  const pinTypeID = pin.pin_type_id;
  const statusId = pin.pin_status_id;

  if (statusId && statusId === PIN_STATUSES.Deleted) return null;

  const translationKey = isNumber(statusId) ? pinStatusTranslations[statusId].translation_key : "";
  const defaultMessage = isNumber(statusId) ? pinStatusTranslations[statusId].default_message : "";

  return (
    <Popup
      trigger={
        <Label color={getPinColor({ pin, vin, location, brands, intervention, questionResult })} onClick={readOnly ? undefined : onClick}>
          <div className="icon-count-wrapper">
            <PinTypeIcon pinTypeId={pinTypeID} /> <span>{intervention?.pin_history?.length}</span>
          </div>
        </Label>
      }
      content={
        <span>
          <strong>{t("v8_pin_status", "Pin Status")}:&nbsp;</strong>
          {t(translationKey) || defaultMessage}
        </span>
      }
    />
  );
};
